import React, { useState } from 'react'

import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Chip,
  Stack,
  TextField,
} from '@mui/material'

import RelatedScreen from '../../../Components/RelatedScreen'
import { useGlobalContext } from '../../../Contexts/GlobalTriggers'
import { useAddSampleContext } from '../../../Contexts/Modules/SampleModule/AddSampleModule'
import {
  useGetLinesInRacks,
  useNewRackSample,
} from '../../../APIs/Hooks/SampleHooks'
import PageSection from '../../../Constants&Theme/PageSection'
import RackCard from './Cards/RackCard'
import SampleIDInput from './SampleIDInput'
import { stackGap } from '../../../Constants&Theme/Spacing'
import InvalidAccessAlert from './InvalidAccessAlert'

function parseRackID(rackBarcode: string) {
  try {
    const regex = /^RACK([0-9]*)/i
    return regex.exec(rackBarcode)![1]
  } catch (error) {
    return rackBarcode
  }
}

function AddRackSample() {
  const { playScanSound } = useGlobalContext()
  const { sampleRun } = useAddSampleContext()

  const [rackKeys, setRackKeys] = useState<any[]>([])
  const [sampleID, setSampleID] = useState('')
  const [validID, setValidID] = useState(false)

  const rackQueries = useGetLinesInRacks([...new Set(rackKeys)], setRackKeys)
  const { mutate: saveSample } = useNewRackSample(
    sampleID,
    rackQueries.map((q) => q.data?.rack?.RackID),
  )

  if (!sampleRun) {
    return <InvalidAccessAlert />
  }
  return (
    <Box
      sx={{
        maxWidth: 'md',
        width: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <RelatedScreen sampleRun={sampleRun} />
      <PageSection title={'Racks'}>
        <Stack
          gap={stackGap.col}
          width={1}
          alignItems={'center'}
        >
          <Autocomplete
            multiple
            value={rackKeys}
            onChange={(_, newValue) => {
              playScanSound(true)
              setRackKeys(newValue)
            }}
            options={[]}
            renderTags={(value: readonly string[], getTagProps) =>
              value.map((option: string, index: number) => (
                <Chip
                  variant="outlined"
                  label={option}
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Scan Racks"
                placeholder="Rack ID"
                onKeyDown={(e: any) => {
                  if (
                    (e.code === 'Enter' || e.code === 'Tab') &&
                    e.target.value
                  ) {
                    playScanSound(true)
                    setRackKeys((rk) => rk.concat(parseRackID(e.target.value)))
                  }
                }}
              />
            )}
            fullWidth
          />
          {rackQueries.length > 0 ?
            rackQueries.map((rackQuery, index) => (
              <RackCard
                rackQuery={rackQuery}
                index={index}
                key={index}
              />
            ))
          : <Alert severity={'info'}>
              <AlertTitle>
                At least one rack is required before saving a sample
              </AlertTitle>
            </Alert>
          }
          <Stack
            gap={stackGap.col}
            width={200}
          >
            <SampleIDInput
              disabled={rackQueries.filter((r) => r.data?.rack).length === 0}
              value={sampleID}
              valid={validID}
              setValue={(e: any) => {
                setValidID(false)
                setSampleID(e?.target?.value ?? '')
              }}
              setValidID={setValidID}
            />
            <Box>
              <Button
                fullWidth
                variant="contained"
                color="success"
                disabled={
                  !(
                    rackQueries.filter((r) => r.data?.rack).length > 0 &&
                    validID
                  )
                }
                onClick={() => saveSample()}
              >
                Save Sample
              </Button>
            </Box>
          </Stack>
        </Stack>
      </PageSection>
    </Box>
  )
}

export default AddRackSample
